/**
 * Thin fetch wrapper that:
 *   - prefixes /api
 *   - sends cookies (sessions) on every call
 *   - fetches a CSRF token from /auth/csrf and forwards it in `x-csrf-token`
 *     for mutating methods, retrying once if the server says it's stale
 *   - throws ApiError on non-2xx responses with a typed payload
 */

export interface ApiErrorPayload {
  code: string;
  message: string;
  details?: unknown;
}

export class ApiError extends Error {
  status: number;
  code: string;
  details?: unknown;
  constructor(status: number, payload: ApiErrorPayload) {
    super(payload.message);
    this.status = status;
    this.code = payload.code;
    this.details = payload.details;
  }
}

/**
 * CSRF token handling.
 *
 * The server uses double-submit-cookie: it sends back an HMAC-encoded value
 * in the `vdpm_csrf` cookie AND the raw token in the JSON body. We never read
 * the cookie value — only the body token, kept in module memory. The cookie
 * is for the server's eyes only.
 */
let csrfToken: string | null = null;
let csrfTokenPromise: Promise<string> | null = null;

async function ensureCsrfToken(force = false): Promise<string> {
  if (csrfToken && !force) return csrfToken;
  if (!csrfTokenPromise) {
    csrfTokenPromise = fetch('/api/auth/csrf', { credentials: 'include' })
      .then((r) => r.json())
      .then((j) => {
        csrfToken = j.csrfToken as string;
        return csrfToken;
      })
      .finally(() => {
        csrfTokenPromise = null;
      });
  }
  return csrfTokenPromise;
}

export function invalidateCsrfToken(): void {
  csrfToken = null;
}

/**
 * Global handler fired when an authenticated request comes back 401. Lets the
 * app drop the stale user and redirect to /login instead of surfacing a raw
 * "Authentication required" error when a session has expired. Registered once
 * from App; auth endpoints are excluded so the login/logout flow is unaffected.
 */
let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(fn: (() => void) | null): void {
  onUnauthorized = fn;
}

type Method = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE' | 'HEAD';

interface RequestOptions {
  method?: Method;
  body?: unknown;
  formData?: FormData;
  signal?: AbortSignal;
  headers?: Record<string, string>;
}

async function attempt<T>(path: string, opts: RequestOptions, retried: boolean): Promise<T> {
  const method = opts.method ?? 'GET';
  const headers: Record<string, string> = { Accept: 'application/json', ...opts.headers };

  if (method !== 'GET' && method !== 'HEAD') {
    headers['x-csrf-token'] = await ensureCsrfToken();
  }

  let body: BodyInit | undefined;
  if (opts.formData) {
    body = opts.formData;
  } else if (opts.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(opts.body);
  }

  const res = await fetch(`/api${path}`, {
    method,
    credentials: 'include',
    headers,
    body,
    signal: opts.signal,
  });

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  let data: ReturnType<typeof JSON.parse>;
  try {
    data = text ? JSON.parse(text) : undefined;
  } catch {
    // An HTML page here usually means the request was misrouted (e.g. the SPA
    // fallback answered) — fail with a readable error, not a SyntaxError.
    throw new ApiError(res.status, {
      code: 'BAD_RESPONSE',
      message: `Server returned an unexpected non-JSON response (HTTP ${res.status}).`,
    });
  }

  if (!res.ok) {
    const payload: ApiErrorPayload = data?.error ?? { code: 'UNKNOWN', message: res.statusText };
    // Stale CSRF token: drop the cached one and retry once with a fresh one.
    // Avoids a confusing failure after long idle / server restart.
    if (res.status === 403 && payload.code === 'EBADCSRFTOKEN' && !retried) {
      invalidateCsrfToken();
      await ensureCsrfToken(true);
      return attempt<T>(path, opts, true);
    }
    // Expired/invalid session on a protected route → let the app redirect to
    // login. Auth endpoints (/auth/me on load, login, etc.) 401 normally, so
    // they're excluded to avoid redirect loops.
    if (res.status === 401 && !path.startsWith('/auth/')) {
      onUnauthorized?.();
    }
    throw new ApiError(res.status, payload);
  }
  return data as T;
}

export async function api<T = unknown>(path: string, opts: RequestOptions = {}): Promise<T> {
  return attempt<T>(path, opts, false);
}

export const apiClient = {
  get: <T>(path: string, signal?: AbortSignal) => api<T>(path, { method: 'GET', signal }),
  post: <T>(path: string, body?: unknown) => api<T>(path, { method: 'POST', body }),
  patch: <T>(path: string, body?: unknown) => api<T>(path, { method: 'PATCH', body }),
  delete: <T>(path: string) => api<T>(path, { method: 'DELETE' }),
  upload: <T>(path: string, formData: FormData) => api<T>(path, { method: 'POST', formData }),
};
